import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { SetExtendedAttributeOptions } from 'app/library/extended-attribute-options/actions';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import { SetUser } from 'app/projects/user/src/lib/actions/set-entity.action';
import { TranslationsGuard } from 'app/services/translations.guard';
import {
    CreateExtendedAttribute,
    CreateExtendedAttributeFailure,
    CreateExtendedAttributeSuccess,
    DeleteExtendedAttribute,
    DeleteExtendedAttributeFailure,
    DeleteExtendedAttributeSuccess,
    FetchExtendedAttribute,
    FetchExtendedAttributeFailure,
    FetchExtendedAttributeSuccess,
    FetchExtendedAttributes,
    FetchExtendedAttributesFailure,
    FetchExtendedAttributesSuccess,
    SetExtendedAttribute,
    SetExtendedAttributeOptionsById,
    SetExtendedAttributes,
    UnsetExtendedAttribute,
    UpdateExtendedAttribute,
    UpdateExtendedAttributeFailure,
    UpdateExtendedAttributeSuccess,
    UpdateExtendedAttributeTranslations,
    UpdateExtendedAttributeTranslationsFailure,
    UpdateExtendedAttributeTranslationsSuccess,
    UpdateExtendedAttributeValue,
    UpdateExtendedAttributeValueFailure,
    UpdateExtendedAttributeValueSuccess,
} from '../actions';
import { AddExtendedAttributes } from '../actions/add-entities.action';
import { ExtendedAttributeApiService } from '../services/extended-attribute.api-service';
import { ExtendedAttribute } from './extended-attribute';
import { ExtendedAttributeStateModel } from './extended-attribute.state-model';

@State<ExtendedAttributeStateModel>({
    name: 'extendedAttribute',
    defaults: new ExtendedAttributeStateModel(),
})
@Injectable()
export class ExtendedAttributeState extends EntityState<ExtendedAttribute> implements IEntityState<ExtendedAttribute> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: ExtendedAttributeStateModel, id: string = state.item): ExtendedAttribute {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: ExtendedAttributeStateModel): ExtendedAttribute[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(ExtendedAttributeState.sortProperties));

        return toReturn;
    }

    constructor(private _extendedAttributeApiService: ExtendedAttributeApiService, private _translationsGuard: TranslationsGuard) {
        super(_extendedAttributeApiService, 30);
    }

    @Action(AddExtendedAttributes)
    addEntities(ctx: StateContext<ExtendedAttributeStateModel>, action: AddExtendedAttributes): void {
        this._addEntities(ctx, action);
    }

    @Action(SetExtendedAttribute)
    setEntity(ctx: StateContext<ExtendedAttributeStateModel>, action: SetExtendedAttribute): void {
        this._setEntity(ctx, action, AddExtendedAttributes);
    }

    @Action(UnsetExtendedAttribute)
    unsetEntity(ctx: StateContext<ExtendedAttributeStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateExtendedAttribute)
    createEntity(ctx: StateContext<ExtendedAttributeStateModel>, action: CreateExtendedAttribute): void {
        this._createEntity(ctx, action, CreateExtendedAttributeSuccess, CreateExtendedAttributeFailure, AddExtendedAttributes).then(() => {});
    }

    @Action(CreateExtendedAttributeSuccess)
    createEntitySuccess(ctx: StateContext<ExtendedAttributeStateModel>, action: CreateExtendedAttributeSuccess): void {
        this._translationsGuard.fetchTranslation(localStorage.getItem('accessToken'));
    }

    @Action(UpdateExtendedAttribute)
    updateEntity(ctx: StateContext<ExtendedAttributeStateModel>, action: UpdateExtendedAttribute): void {
        this._updateEntity(ctx, action, UpdateExtendedAttributeSuccess, UpdateExtendedAttributeFailure, AddExtendedAttributes).then(() => {});
    }

    @Action(UpdateExtendedAttributeTranslations)
    updateExtendedAttributeTranslations(ctx: StateContext<ExtendedAttributeStateModel>, action: UpdateExtendedAttributeTranslations): void {
        this._extendedAttributeApiService.updateTranslations(action.payload).subscribe(
            (entity) => {
                ctx.dispatch([new AddExtendedAttributes([entity]), new UpdateExtendedAttributeTranslationsSuccess(entity)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdateExtendedAttributeTranslationsFailure({ entityId: action.payload.id, response }));
            }
        );
    }

    @Action(UpdateExtendedAttributeTranslationsSuccess)
    updateExtendedAttributeTranslationsSuccess(ctx: StateContext<ExtendedAttributeStateModel>, action: UpdateExtendedAttributeTranslationsSuccess): void {
        this._translationsGuard.fetchTranslation(localStorage.getItem('accessToken'));
    }

    @Action(DeleteExtendedAttribute)
    deleteEntity(ctx: StateContext<ExtendedAttributeStateModel>, action: DeleteExtendedAttribute): void {
        this._deleteEntity(ctx, action, DeleteExtendedAttributeSuccess, DeleteExtendedAttributeFailure).then(() => {});
    }

    @Action(FetchExtendedAttribute)
    fetchEntity(ctx: StateContext<ExtendedAttributeStateModel>, action: FetchExtendedAttribute): void {
        this._fetchEntity(ctx, action, FetchExtendedAttributeSuccess, FetchExtendedAttributeFailure, AddExtendedAttributes, SetExtendedAttribute).then(() => {});
    }

    @Action(FetchExtendedAttributeSuccess)
    fetchEntitySuccess(ctx: StateContext<ExtendedAttributeStateModel>, action: FetchExtendedAttributeSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchExtendedAttributeFailure)
    fetchEntityFailure(ctx: StateContext<ExtendedAttributeStateModel>, action: FetchExtendedAttributeFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetExtendedAttributes)
    setEntities(ctx: StateContext<ExtendedAttributeStateModel>, action: SetExtendedAttributes): void {
        this._setEntities(ctx, action);
    }

    @Action(FetchExtendedAttributes)
    fetchEntities(ctx: StateContext<ExtendedAttributeStateModel>): void {
        this._fetchEntities(ctx, FetchExtendedAttributesSuccess, FetchExtendedAttributesFailure, SetExtendedAttributes).then(() => {});
    }

    @Action(FetchExtendedAttributesSuccess)
    fetchEntitiesSuccess(ctx: StateContext<ExtendedAttributeStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(FetchExtendedAttributesFailure)
    fetchEntitiesFailure(ctx: StateContext<ExtendedAttributeStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }

    @Action(SetExtendedAttributeOptionsById)
    setExtendedAttributeOptionsById(ctx: StateContext<ExtendedAttributeStateModel>, { payload }: SetExtendedAttributeOptionsById): void {
        const extendedAttribute = ctx.getState().map.get(payload);

        ctx.dispatch(new SetExtendedAttributeOptions(extendedAttribute.options));
    }

    @Action(UpdateExtendedAttributeValue)
    updateExtendedAttributeValue(ctx: StateContext<ExtendedAttributeStateModel>, { payload }: UpdateExtendedAttributeValue): void {
        this._extendedAttributeApiService.updateExtendedAttributeValue(payload).subscribe(
            (entity) => {
                ctx.dispatch([new SetUser(entity), new UpdateExtendedAttributeValueSuccess(entity)]);
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new UpdateExtendedAttributeValueFailure());
            }
        );
    }
}
